import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';

const NotificationSettings = ({ isOpen, onClose }) => {
  const [settings, setSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    reminderTiming: '7',
    eventTypes: ['applications', 'exams', 'results'],
    institutionTypes: ['all']
  });

  if (!isOpen) return null;

  const reminderOptions = [
    { value: '1', label: '1 jour avant' },
    { value: '3', label: '3 jours avant' },
    { value: '7', label: '1 semaine avant' },
    { value: '14', label: '2 semaines avant' },
    { value: '30', label: '1 mois avant' }
  ];

  const eventTypes = [
    { key: 'applications', label: 'Candidatures', description: 'Dates limites de candidature' },
    { key: 'exams', label: 'Concours', description: 'Dates d\'examens et concours' },
    { key: 'results', label: 'Résultats', description: 'Publication des résultats' },
    { key: 'interviews', label: 'Entretiens', description: 'Convocations aux entretiens' },
    { key: 'registration', label: 'Inscriptions', description: 'Périodes d\'inscription' }
  ];

  const institutionTypes = [
    { value: 'all', label: 'Tous les établissements' },
    { value: 'favorites', label: 'Mes établissements favoris uniquement' },
    { value: 'applied', label: 'Établissements où j\'ai candidaté' }
  ];

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleEventTypeToggle = (eventType) => {
    const newEventTypes = settings?.eventTypes?.includes(eventType)
      ? settings?.eventTypes?.filter(type => type !== eventType)
      : [...settings?.eventTypes, eventType];
    
    handleSettingChange('eventTypes', newEventTypes);
  };

  const handleSave = () => {
    // Save settings logic here
    console.log('Saving notification settings:', settings);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card border border-border rounded-lg shadow-interactive max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-heading font-bold text-foreground">
            Paramètres de notification
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-smooth"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Notification Methods */}
          <div>
            <h3 className="font-medium text-foreground mb-4">Méthodes de notification</h3>
            <div className="space-y-3">
              <Checkbox
                label="Notifications par email"
                description="Recevoir les alertes par email"
                checked={settings?.emailNotifications}
                onChange={(e) => handleSettingChange('emailNotifications', e?.target?.checked)}
              />
              <Checkbox
                label="Notifications SMS"
                description="Recevoir les alertes par SMS (frais d'opérateur applicables)"
                checked={settings?.smsNotifications}
                onChange={(e) => handleSettingChange('smsNotifications', e?.target?.checked)}
              />
              <Checkbox
                label="Notifications push"
                description="Recevoir les notifications dans le navigateur"
                checked={settings?.pushNotifications}
                onChange={(e) => handleSettingChange('pushNotifications', e?.target?.checked)}
              />
            </div>
          </div>

          {/* Reminder Timing */}
          <div>
            <Select
              label="Délai de rappel"
              description="Quand souhaitez-vous être notifié avant une échéance ?"
              options={reminderOptions}
              value={settings?.reminderTiming}
              onChange={(value) => handleSettingChange('reminderTiming', value)}
            />
          </div>

          {/* Event Types */}
          <div>
            <h3 className="font-medium text-foreground mb-4">Types d'événements</h3>
            <div className="space-y-3">
              {eventTypes?.map((eventType) => (
                <Checkbox
                  key={eventType?.key}
                  label={eventType?.label}
                  description={eventType?.description}
                  checked={settings?.eventTypes?.includes(eventType?.key)}
                  onChange={() => handleEventTypeToggle(eventType?.key)}
                />
              ))}
            </div>
          </div>

          {/* Institution Filter */}
          <div>
            <Select
              label="Établissements à suivre"
              description="Choisissez quels établissements vous souhaitez suivre"
              options={institutionTypes}
              value={settings?.institutionTypes?.[0]}
              onChange={(value) => handleSettingChange('institutionTypes', [value])}
            />
          </div>

          {/* Preview */}
          <div className="bg-muted/50 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={16} className="text-primary mt-0.5" />
              <div>
                <h4 className="font-medium text-foreground mb-1">Aperçu des notifications</h4>
                <p className="text-sm text-muted-foreground">
                  Vous recevrez des notifications {settings?.reminderTiming} jour{settings?.reminderTiming !== '1' ? 's' : ''} avant 
                  les échéances pour {settings?.eventTypes?.length} type{settings?.eventTypes?.length !== 1 ? 's' : ''} d'événements.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-6 border-t border-border bg-muted/50">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1"
          >
            Annuler
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
            iconName="Check"
            iconPosition="left"
            className="flex-1"
          >
            Enregistrer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;